import React, { useEffect, useMemo, useState } from "react";
import { Search, ChevronDown, RefreshCw, Pencil, X, Lock, Unlock } from "lucide-react";
import { apiFetch } from "../utils/api";

interface WagerRow {
  id: number;
  name: string;
  email: string;
  wager: number;
  wager_adjustment?: number;
  total_deposit: number;
  target: number;
  target_percentage: number;
  wager_multiplier?: number;
  is_locked?: boolean;
  deposit_bonus_claimed?: boolean;
}

interface ApiResponse {
  success: boolean;
  data?: any;
  message?: string;
}

const fmtAmt = (n: number | null | undefined) => {
  if (n == null || Number.isNaN(n)) return "-";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format(n);
};

const clampPct = (n: number) => Math.max(0, Math.min(100, n));

export default function UserWagerReports() {
  const [rows, setRows] = useState<WagerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState<keyof WagerRow>("target_percentage");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [globalTargetX, setGlobalTargetX] = useState<number>(3);
  const [editingUser, setEditingUser] = useState<WagerRow | null>(null);
  const [userTargetX, setUserTargetX] = useState<number>(3);
  const [editingWager, setEditingWager] = useState<WagerRow | null>(null);
  const [wagerValue, setWagerValue] = useState<number>(0);
  const [savingWager, setSavingWager] = useState(false);
  const pageSize = 10;

  const columns = [
    { key: "id", label: "User ID" },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "wager", label: "Wager" },
    { key: "total_deposit", label: "Total Deposit" },
    { key: "target", label: "Target" },
    { key: "target_percentage", label: "% Achieved" },
  ] as const;

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await apiFetch<ApiResponse>("/api/deposits/admin/all-wagers");

      if (!res?.success) throw new Error("Failed to fetch data");

      setRows(res.data || []);

    } catch (e: any) {
      setError(e?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const base = term
      ? rows.filter(r =>
          r.name.toLowerCase().includes(term) ||
          r.email.toLowerCase().includes(term) ||
          String(r.id).includes(term)
        )
      : rows;

    const sorted = [...base].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return sortDir === "asc" ? -1 : 1;
      if (vb == null) return sortDir === "asc" ? 1 : -1;

      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }

      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return sortDir === "asc" ? (sa < sb ? -1 : 1) : (sa > sb ? -1 : 1);
    });

    return sorted;
  }, [rows, q, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const onSort = (key: keyof WagerRow) => {
    if (key === sortKey) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  // Update all users' target multiplier (only unlocked ones)
  const updateAllTargetX = async () => {
    try {
      if (!globalTargetX || globalTargetX <= 0) {
        return;
      }

      const res = await apiFetch(`/api/deposits/admin/update-all-targetx`, {
        method: "POST",
        body: JSON.stringify({ multiplier: globalTargetX })
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to update multiplier");
      }

      fetchData();
    } catch (err: any) {
      console.log(err)
    }
  };

  // Update individual user's target multiplier
  const updateUserTargetX = async () => {
    try {
      if (!userTargetX || !editingUser) {
        alert("Please enter a valid multiplier.");
        return;
      }

      const res = await apiFetch(`/api/deposits/admin/targetx/${editingUser.id}`, {
        method: "POST",
        body: JSON.stringify({ multiplier: userTargetX })
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to update user multiplier");
      }

      setEditingUser(null);
      fetchData();
    } catch (err: any) {
      alert(err.message || "Something went wrong");
    }
  };

  // Manually set a user's wagering total (offset model — future wagering keeps adding on top)
  const updateUserWagerValue = async () => {
    try {
      if (!editingWager) return;
      if (!Number.isFinite(wagerValue) || wagerValue < 0) {
        alert("Please enter a valid wager amount.");
        return;
      }

      setSavingWager(true);
      const res = await apiFetch(`/api/deposits/admin/wager-override/${editingWager.id}`, {
        method: "POST",
        body: JSON.stringify({ wager: wagerValue })
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to update wager");
      }

      setEditingWager(null);
      fetchData();
    } catch (err: any) {
      alert(err.message || "Something went wrong");
    } finally {
      setSavingWager(false);
    }
  };

  // Toggle lock status for a user
  const toggleLock = async (userId: number, currentLockStatus: boolean) => {
     console.log(currentLockStatus, 'current lock status')
    try {
      const res = await apiFetch(`/api/deposits/admin/targetx/${userId}/lock`, {
        method: "POST",
        body: JSON.stringify({ is_locked: !currentLockStatus })
      });

      console.log(res)

      if (!res?.success) {
        throw new Error(res?.message || "Failed to toggle lock");
      }

      // Update local state immediately for better UX
      setRows(prevRows =>
        prevRows.map(row =>
          row.id === userId
            ? { ...row, is_locked: !currentLockStatus }
            : row
        )
      );
    } catch (err: any) {
      console.error("Lock toggle error:", err);
      // Refresh data on error to ensure consistency
      fetchData();
    }
  };

  return (
    <div className="ml-0 lg:ml-[256px] p-2">
      <div className="max-w-7xl mx-auto p-6 space-y-6 bg-gray-50 min-h-screen">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">User Turnover Report</h1>
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition"
          >
            <RefreshCw className="w-5 h-5" /> Refresh
          </button>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => { setPage(1); setQ(e.target.value); }}
              placeholder="Search by name, email, or ID"
              className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              value={globalTargetX}
              onChange={(e) => setGlobalTargetX(Number(e.target.value))}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="3"
            />
            <button
              onClick={updateAllTargetX}
              className="rounded-lg bg-green-600 text-white px-4 py-2 hover:bg-green-700 transition whitespace-nowrap"
            >
              Update All TargetX
            </button>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Sort by</label>
            <select
              value={String(sortKey)}
              onChange={(e) => setSortKey(e.target.value as keyof WagerRow)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              {columns.map(c => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <button
              onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              title="Toggle sort direction"
            >
              {sortDir === "asc" ? "Asc" : "Desc"}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-gray-600 text-lg animate-pulse">Loading wagers…</div>
        ) : error ? (
          <div className="text-red-600">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="text-gray-600">No results.</div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  {columns.map(c => (
                    <th
                      key={c.key}
                      onClick={() => onSort(c.key as keyof WagerRow)}
                      className="px-4 py-3 font-semibold text-left cursor-pointer select-none hover:bg-gray-200 transition"
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {sortKey === c.key && (
                          <ChevronDown className={`w-4 h-4 transition-transform ${sortDir === "desc" ? "rotate-180" : ""}`} />
                        )}
                      </span>
                    </th>
                  ))}
                  <th className="px-4 py-3 font-semibold text-left">Progress</th>
                  <th className="px-4 py-3 font-semibold text-left">Deposit Bonus</th>
                  <th className="px-4 py-3 font-semibold text-left">Lock</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  const pct = clampPct(r.target_percentage);
                  const over = r.target_percentage > 100 ? `+${(r.target_percentage - 100).toFixed(1)}%` : "";

                  return (
                    <tr key={r.id} className="border-t hover:bg-gray-50 transition">
                      <td className="px-4 py-3 whitespace-nowrap text-gray-900">{r.id}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{r.name}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">{r.email}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span>{fmtAmt(r.wager)}</span>
                          <button
                            onClick={() => { setEditingWager(r); setWagerValue(r.wager); }}
                            className="text-blue-600 hover:text-blue-800 transition"
                            title="Manually adjust wager"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {!!r.wager_adjustment && (
                            <span
                              className="text-xs font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded"
                              title="Manual adjustment applied"
                            >
                              {r.wager_adjustment > 0 ? "+" : ""}{fmtAmt(r.wager_adjustment)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtAmt(r.total_deposit)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span>{fmtAmt(r.target)}</span>
                          <button
                            onClick={() => {
                              setEditingUser(r);
                              setUserTargetX(r.wager_multiplier || 3);
                            }}
                            className="text-blue-600 hover:text-blue-800 transition inline-flex items-center gap-1"
                            title="Edit target multiplier"
                          >
                            <Pencil className="w-4 h-4" />
                            {r.wager_multiplier && (
                              <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">
                                {r.wager_multiplier}x
                              </span>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={r.target_percentage >= 100 ? "text-green-600 font-medium" : ""}>
                          {r.target_percentage.toFixed(2)}%
                          {over && <span className="text-green-500 ml-1"> {over}</span>}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1 min-w-[200px]">
                          <div className="w-full h-3 rounded-full bg-gray-200 overflow-hidden relative">
                            <div
                              className={`h-full transition-all duration-300 ${r.target_percentage >= 100 ? "bg-green-500" : "bg-blue-600"}`}
                              style={{ width: `${pct}%` }}
                            />
                            {r.target_percentage > 100 && (
                              <div className="absolute inset-0 flex items-center justify-end pr-1">
                                <span className="text-xs text-white font-bold">100%+</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-600">
                            <span>{fmtAmt(r.wager)}</span>
                            <span className="font-medium">{r.target_percentage.toFixed(1)}%</span>
                            <span>{fmtAmt(r.target)}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {r.deposit_bonus_claimed ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                            ✓ Claimed {r.wager_multiplier ? `(${r.wager_multiplier}x)` : ""}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                            Not claimed
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          onClick={() => toggleLock(r.id, r.is_locked || false)}
                          className={`p-2 rounded-lg transition ${
                            r.is_locked
                              ? "bg-red-100 text-red-600 hover:bg-red-200"
                              : "bg-green-100 text-green-600 hover:bg-green-200"
                          }`}
                          title={r.is_locked ? "Locked - Click to unlock" : "Unlocked - Click to lock"}
                        >
                          {r.is_locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {filtered.length > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Showing {Math.min((page - 1) * pageSize + 1, filtered.length)}–
              {Math.min(page * pageSize, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="px-3 py-1 rounded-lg border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >Previous</button>
              <span>Page {page} / {totalPages}</span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                className="px-3 py-1 rounded-lg border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Edit User Target Popup */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Edit Target Multiplier
              </h3>
              <button
                onClick={() => setEditingUser(null)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">User ID: <span className="font-medium text-gray-900">{editingUser.id}</span></p>
                <p className="text-sm text-gray-600">Name: <span className="font-medium text-gray-900">{editingUser.name}</span></p>
                <p className="text-sm text-gray-600">Email: <span className="font-medium text-gray-900">{editingUser.email}</span></p>
                {editingUser.wager_multiplier && (
                  <p className="text-sm text-gray-600">Current Multiplier: <span className="font-medium text-blue-600">{editingUser.wager_multiplier}x</span></p>
                )}
                <p className="text-sm text-gray-600">
                  Status: <span className={`font-medium ${editingUser.is_locked ? "text-red-600" : "text-green-600"}`}>
                    {editingUser.is_locked ? "Locked" : "Unlocked"}
                  </span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Multiplier
                </label>
                <input
                  type="number"
                  min="1"
                  value={userTargetX}
                  onChange={(e) => setUserTargetX(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="3"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setEditingUser(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={updateUserTargetX}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  Update TargetX
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manually adjust wager popup */}
      {editingWager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Adjust Wager</h3>
              <button
                onClick={() => setEditingWager(null)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">User ID: <span className="font-medium text-gray-900">{editingWager.id}</span></p>
                <p className="text-sm text-gray-600">Name: <span className="font-medium text-gray-900">{editingWager.name}</span></p>
                <p className="text-sm text-gray-600">Email: <span className="font-medium text-gray-900">{editingWager.email}</span></p>
                <p className="text-sm text-gray-600">Current Wager: <span className="font-medium text-blue-600">{fmtAmt(editingWager.wager)}</span></p>
                {!!editingWager.wager_adjustment && (
                  <p className="text-sm text-gray-600">Current Adjustment: <span className="font-medium text-amber-600">{editingWager.wager_adjustment > 0 ? "+" : ""}{fmtAmt(editingWager.wager_adjustment)}</span></p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Wager Total
                </label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={wagerValue}
                  onChange={(e) => setWagerValue(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
                <p className="mt-2 text-xs text-gray-500">
                  This sets the displayed total now. Future real wagering keeps adding on top of this value — it won't freeze.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setEditingWager(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={updateUserWagerValue}
                  disabled={savingWager}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                  {savingWager ? "Saving…" : "Save Wager"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// import React, { useEffect, useMemo, useState } from "react";
// import { ChevronDownIcon, ArrowPathIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
// import { apiFetch } from "../utils/api";

// import { Search, ChevronDown, RefreshCw, Pencil, X } from "lucide-react";

// // ──────────────────────────────────────────────────────────────
// //  UPDATED TYPE: Added wager_multiplier field
// // ──────────────────────────────────────────────────────────────

// interface WagerRow {
//   id: number;
//   name: string;
//   email: string;
//   wager: number;
//   total_deposit: number;
//   target: number;
//   target_percentage: number;
//   wager_multiplier?: number; // NEW: User-specific multiplier
// }

// interface ApiResponse {
//   success: boolean;
//   data?: any;
//   message?: string;
// }

// // ──────────────────────────────────────────────────────────────
// //  Formatters
// // ──────────────────────────────────────────────────────────────

// const fmtDateTime = (iso: string | null) => {
//   if (!iso) return "-";
//   const d = new Date(iso);
//   if (Number.isNaN(d.getTime())) return "-";
//   return d.toLocaleString();
// };

// const fmtAmt = (n: number | null | undefined) => {
//   if (n == null || Number.isNaN(n)) return "-";
//   return new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format(n);
// };

// const clampPct = (n: number) => Math.max(0, Math.min(100, n));

// export default function UserWagerReports() {
//   const [rows, setRows] = useState<WagerRow[]>([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState<string | null>(null);
//   const [q, setQ] = useState("");
//   const [sortKey, setSortKey] = useState<keyof WagerRow>("target_percentage");
//   const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
//   const [page, setPage] = useState(1);
//   const [targetX, setTargetX] = useState<number>(3);
//   const [editingUser, setEditingUser] = useState<WagerRow | null>(null);
//   const [userTargetX, setUserTargetX] = useState<number>(3);
//   const pageSize = 10;

//   const columns = [
//     { key: "id", label: "User ID" },
//     { key: "name", label: "Name" },
//     { key: "email", label: "Email" },
//     { key: "wager", label: "Wager" },
//     { key: "total_deposit", label: "Total Deposit" },
//     { key: "target", label: `Targetx(${targetX})` },
//     { key: "target_percentage", label: "% Achieved" },
//   ] as const;

//   const fetchData = async () => {
//     try {
//       setLoading(true);
//       setError(null);

//       const res = await apiFetch<ApiResponse>("/api/deposits/admin/all-wagers");
//       const res2 = await apiFetch<ApiResponse>("/api/deposits/admin/common-targetx");

//       if (!res?.success || !res2?.success) throw new Error("Failed to fetch data");

//       setRows(res.data || []);
//       setTargetX(res2.data[0]?.multiplier);

//     } catch (e: any) {
//       try {
//         const fallback1 = await apiFetch<ApiResponse>("/api/deposits/admin/all-wagers");
//         const fallback2 = await apiFetch<ApiResponse>("/api/deposits/admin/common-targetx");

//         if (!fallback1?.success || !fallback2?.success) throw new Error("Fallback failed");

//         setRows(fallback1.data || []);
//         setTargetX(fallback2.data[0]?.multiplier);

//       } catch (e2: any) {
//         setError(e2?.message || "Failed to load data");
//       }
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchData();
//   }, []);

//   const filtered = useMemo(() => {
//     const term = q.trim().toLowerCase();
//     const base = term
//       ? rows.filter(r =>
//           r.name.toLowerCase().includes(term) ||
//           r.email.toLowerCase().includes(term) ||
//           String(r.id).includes(term)
//         )
//       : rows;

//     const sorted = [...base].sort((a, b) => {
//       const va = a[sortKey];
//       const vb = b[sortKey];
//       if (va == null && vb == null) return 0;
//       if (va == null) return sortDir === "asc" ? -1 : 1;
//       if (vb == null) return sortDir === "asc" ? 1 : -1;

//       if (typeof va === "number" && typeof vb === "number") {
//         return sortDir === "asc" ? va - vb : vb - va;
//       }

//       const sa = String(va).toLowerCase();
//       const sb = String(vb).toLowerCase();
//       return sortDir === "asc" ? (sa < sb ? -1 : 1) : (sa > sb ? -1 : 1);
//     });

//     return sorted;
//   }, [rows, q, sortKey, sortDir]);

//   const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
//   const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

//   const onSort = (key: keyof WagerRow) => {
//     if (key === sortKey) {
//       setSortDir(d => (d === "asc" ? "desc" : "asc"));
//     } else {
//       setSortKey(key);
//       setSortDir("desc");
//     }
//   };

//   const updateTargetX = async() => {
//     try {
//       if (!targetX) {
//         alert("Please enter a multiplier.");
//         return;
//       }

//       const res = await apiFetch(`/api/deposits/admin/targetx`, {
//         method: "POST",
//         body: JSON.stringify({multiplier: targetX })
//       });

//       if (!res?.success) {
//         throw new Error(res?.message || "Failed to update multiplier");
//       }

//       fetchData();
//     } catch (err: any) {
//       alert(err.message || "Something went wrong");
//     }
//   };

//   const updateUserTargetX = async() => {
//     try {
//       if (!userTargetX || !editingUser) {
//         alert("Please enter a valid multiplier.");
//         return;
//       }

//       const res = await apiFetch(`/api/deposits/admin/targetx/${editingUser.id}`, {
//         method: "POST",
//         body: JSON.stringify({multiplier: userTargetX })
//       });

//       if (!res?.success) {
//         throw new Error(res?.message || "Failed to update user multiplier");
//       }

//       setEditingUser(null);
//       fetchData();
//     } catch (err: any) {
//       alert(err.message || "Something went wrong");
//     }
//   };

//   return (
//     <div className="ml-0 lg:ml-[256px] p-2">
//       <div className="max-w-7xl mx-auto p-6 space-y-6 bg-gray-50 min-h-screen">
//         <div className="flex items-center justify-between">
//           <h1 className="text-3xl font-bold text-gray-900">User Turnover Report</h1>
//           <button
//             onClick={fetchData}
//             className="inline-flex items-center gap-2 rounded-lg bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition"
//           >
//             <RefreshCw className="w-5 h-5" /> Refresh
//           </button>
//         </div>

//         <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
//           <div className="relative w-full sm:w-80">
//             <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
//             <input
//               value={q}
//               onChange={(e) => { setPage(1); setQ(e.target.value); }}
//               placeholder="Search by name, email, or ID"
//               className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           <div className="flex items-center gap-2">
//             <input
//               type="number"
//               min="1"
//               value={targetX}
//               onChange={(e) => setTargetX(Number(e.target.value))}
//               className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="3"
//             />
//             <button
//               onClick={updateTargetX}
//               className="rounded-lg bg-green-600 text-white px-4 py-2 hover:bg-green-700 transition"
//             >
//               Update TargetX
//             </button>
//           </div>

//           <div className="flex items-center gap-2">
//             <label className="text-sm text-gray-600">Sort by</label>
//             <select
//               value={String(sortKey)}
//               onChange={(e) => setSortKey(e.target.value as keyof WagerRow)}
//               className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
//             >
//               {columns.map(c => (
//                 <option key={c.key} value={c.key}>{c.label}</option>
//               ))}
//             </select>
//             <button
//               onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
//               className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
//               title="Toggle sort direction"
//             >
//               {sortDir === "asc" ? "Asc" : "Desc"}
//             </button>
//           </div>
//         </div>

//         {loading ? (
//           <div className="text-gray-600 text-lg animate-pulse">Loading wagers…</div>
//         ) : error ? (
//           <div className="text-red-600">{error}</div>
//         ) : filtered.length === 0 ? (
//           <div className="text-gray-600">No results.</div>
//         ) : (
//           <div className="overflow-x-auto bg-white rounded-lg shadow">
//             <table className="min-w-full text-sm">
//               <thead className="bg-gray-100 text-gray-700">
//                 <tr>
//                   {columns.map(c => (
//                     <th
//                       key={c.key}
//                       onClick={() => onSort(c.key as keyof WagerRow)}
//                       className="px-4 py-3 font-semibold text-left cursor-pointer select-none hover:bg-gray-200 transition"
//                     >
//                       <span className="inline-flex items-center gap-1">
//                         {c.label}
//                         {sortKey === c.key && (
//                           <ChevronDown className={`w-4 h-4 transition-transform ${sortDir === "desc" ? "rotate-180" : ""}`} />
//                         )}
//                       </span>
//                     </th>
//                   ))}
//                   <th className="px-4 py-3 font-semibold text-left">Progress</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {paged.map((r) => {
//                   const pct = clampPct(r.target_percentage);
//                   const over = r.target_percentage > 100 ? `+${(r.target_percentage - 100).toFixed(1)}%` : "";

//                   return (
//                     <tr key={r.id} className="border-t hover:bg-gray-50 transition">
//                       <td className="px-4 py-3 whitespace-nowrap text-gray-900">{r.id}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">{r.name}</td>
//                       <td className="px-4 py-3 whitespace-nowrap text-gray-700">{r.email}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">{fmtAmt(r.wager)}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">{fmtAmt(r.total_deposit)}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">
//                         <div className="flex items-center gap-2">
//                           <span>{fmtAmt(r.target)}</span>
//                           <button
//                             onClick={() => {
//                               setEditingUser(r);
//                               setUserTargetX(r.wager_multiplier || targetX);
//                             }}
//                             className="text-blue-600 hover:text-blue-800 transition inline-flex items-center gap-1"
//                             title="Edit target multiplier"
//                           >
//                             <Pencil className="w-4 h-4" />
//                             {r.wager_multiplier && (
//                               <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">
//                                 {r.wager_multiplier}x
//                               </span>
//                             )}
//                           </button>
//                         </div>
//                       </td>
//                       <td className="px-4 py-3 whitespace-nowrap">
//                         <span className={r.target_percentage >= 100 ? "text-green-600 font-medium" : ""}>
//                           {r.target_percentage.toFixed(2)}%
//                           {over && <span className="text-green-500 ml-1"> {over}</span>}
//                         </span>
//                       </td>
//                       <td className="px-4 py-3">
//                         <div className="flex flex-col gap-1 min-w-[200px]">
//                           <div className="w-full h-3 rounded-full bg-gray-200 overflow-hidden relative">
//                             <div
//                               className={`h-full transition-all duration-300 ${r.target_percentage >= 100 ? "bg-green-500" : "bg-blue-600"}`}
//                               style={{ width: `${pct}%` }}
//                             />
//                             {r.target_percentage > 100 && (
//                               <div className="absolute inset-0 flex items-center justify-end pr-1">
//                                 <span className="text-xs text-white font-bold">100%+</span>
//                               </div>
//                             )}
//                           </div>
//                           <div className="flex items-center justify-between text-xs text-gray-600">
//                             <span>{fmtAmt(r.wager)}</span>
//                             <span className="font-medium">{r.target_percentage.toFixed(1)}%</span>
//                             <span>{fmtAmt(r.target)}</span>
//                           </div>
//                         </div>
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>
//         )}

//         {filtered.length > 0 && (
//           <div className="flex items-center justify-between text-sm text-gray-600">
//             <span>
//               Showing {Math.min((page - 1) * pageSize + 1, filtered.length)}–
//               {Math.min(page * pageSize, filtered.length)} of {filtered.length}
//             </span>
//             <div className="flex items-center gap-2">
//               <button
//                 disabled={page <= 1}
//                 onClick={() => setPage(p => Math.max(1, p - 1))}
//                 className="px-3 py-1 rounded-lg border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
//               >Previous</button>
//               <span>Page {page} / {totalPages}</span>
//               <button
//                 disabled={page >= totalPages}
//                 onClick={() => setPage(p => Math.min(totalPages, p + 1))}
//                 className="px-3 py-1 rounded-lg border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
//               >Next</button>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Edit User Target Popup */}
//       {editingUser && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
//             <div className="flex items-center justify-between mb-4">
//               <h3 className="text-xl font-semibold text-gray-900">
//                 Edit Target Multiplier
//               </h3>
//               <button
//                 onClick={() => setEditingUser(null)}
//                 className="text-gray-400 hover:text-gray-600 transition"
//               >
//                 <X className="w-6 h-6" />
//               </button>
//             </div>

//             <div className="space-y-4">
//               <div>
//                 <p className="text-sm text-gray-600">User ID: <span className="font-medium text-gray-900">{editingUser.id}</span></p>
//                 <p className="text-sm text-gray-600">Name: <span className="font-medium text-gray-900">{editingUser.name}</span></p>
//                 <p className="text-sm text-gray-600">Email: <span className="font-medium text-gray-900">{editingUser.email}</span></p>
//                 {editingUser.wager_multiplier && (
//                   <p className="text-sm text-gray-600">Current Multiplier: <span className="font-medium text-blue-600">{editingUser.wager_multiplier}x</span></p>
//                 )}
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Target Multiplier
//                 </label>
//                 <input
//                   type="number"
//                   min="1"
//                   value={userTargetX}
//                   onChange={(e) => setUserTargetX(Number(e.target.value))}
//                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   placeholder="3"
//                 />
//               </div>

//               <div className="flex gap-3">
//                 <button
//                   onClick={() => setEditingUser(null)}
//                   className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   onClick={updateUserTargetX}
//                   className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
//                 >
//                   Update TargetX
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// import React, { useEffect, useMemo, useState } from "react";
// import { ChevronDownIcon, ArrowPathIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
// import { apiFetch } from "../utils/api";

// import { Search, ChevronDown, RefreshCw, Pencil, X } from "lucide-react";

// ──────────────────────────────────────────────────────────────
//  NEW TYPE: Matches your new backend API
// ──────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────
//  Formatters
// ──────────────────────────────────────────────────────────────


// const fmtDateTime = (iso: string | null) => {
//   if (!iso) return "-";
//   const d = new Date(iso);
//   if (Number.isNaN(d.getTime())) return "-";
//   return d.toLocaleString();
// };

// // Clamp percentage to 0–100 for progress bar (optional: allow >100)

// // ──────────────────────────────────────────────────────────────
// //  Columns (updated)
// // ──────────────────────────────────────────────────────────────



// // Mock types for demonstration
// interface WagerRow {
//   id: number;
//   name: string;
//   email: string;
//   wager: number;
//   total_deposit: number;
//   target: number;
//   target_percentage: number;
// }

// interface ApiResponse {
//   success: boolean;
//   data?: any;
//   message?: string;
// }


// const fmtAmt = (n: number | null | undefined) => {
//   if (n == null || Number.isNaN(n)) return "-";
//   return new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format(n);
// };

// const clampPct = (n: number) => Math.max(0, Math.min(100, n));

// export default function UserWagerReports() {
//   const [rows, setRows] = useState<WagerRow[]>([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState<string | null>(null);
//   const [q, setQ] = useState("");
//   const [sortKey, setSortKey] = useState<keyof WagerRow>("target_percentage");
//   const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
//   const [page, setPage] = useState(1);
//   const [targetX, setTargetX] = useState<number>(3);
//   const [editingUser, setEditingUser] = useState<WagerRow | null>(null);
//   const [userTargetX, setUserTargetX] = useState<number>(3);
//   const pageSize = 10;

//   const columns = [
//     { key: "id", label: "User ID" },
//     { key: "name", label: "Name" },
//     { key: "email", label: "Email" },
//     { key: "wager", label: "Wager" },
//     { key: "total_deposit", label: "Total Deposit" },
//     { key: "target", label: `Targetx(${targetX})` },
//     { key: "target_percentage", label: "% Achieved" },
//   ] as const;

//   const fetchData = async () => {
//     try {
//       setLoading(true);
//       setError(null);

//       const res = await apiFetch<ApiResponse>("/api/deposits/admin/all-wagers");
//       const res2 = await apiFetch<ApiResponse>("/api/deposits/admin/common-targetx");

//       if (!res?.success || !res2?.success) throw new Error("Failed to fetch data");

//       setRows(res.data || []);
//       setTargetX(res2.data[0]?.multiplier);

//     } catch (e: any) {
//       try {
//         const fallback1 = await apiFetch<ApiResponse>("https://api.growl.games/api/deposits/admin/all-wagers");
//         const fallback2 = await apiFetch<ApiResponse>("https://api.growl.games/api/deposits/admin/common-targetx");

//         if (!fallback1?.success || !fallback2?.success) throw new Error("Fallback failed");

//         setRows(fallback1.data || []);
//         setTargetX(fallback2.data[0]?.multiplier);

//       } catch (e2: any) {
//         setError(e2?.message || "Failed to load data");
//       }
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchData();
//   }, []);

//   const filtered = useMemo(() => {
//     const term = q.trim().toLowerCase();
//     const base = term
//       ? rows.filter(r =>
//           r.name.toLowerCase().includes(term) ||
//           r.email.toLowerCase().includes(term) ||
//           String(r.id).includes(term)
//         )
//       : rows;

//     const sorted = [...base].sort((a, b) => {
//       const va = a[sortKey];
//       const vb = b[sortKey];
//       if (va == null && vb == null) return 0;
//       if (va == null) return sortDir === "asc" ? -1 : 1;
//       if (vb == null) return sortDir === "asc" ? 1 : -1;

//       if (typeof va === "number" && typeof vb === "number") {
//         return sortDir === "asc" ? va - vb : vb - va;
//       }

//       const sa = String(va).toLowerCase();
//       const sb = String(vb).toLowerCase();
//       return sortDir === "asc" ? (sa < sb ? -1 : 1) : (sa > sb ? -1 : 1);
//     });

//     return sorted;
//   }, [rows, q, sortKey, sortDir]);

//   const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
//   const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

//   const onSort = (key: keyof WagerRow) => {
//     if (key === sortKey) {
//       setSortDir(d => (d === "asc" ? "desc" : "asc"));
//     } else {
//       setSortKey(key);
//       setSortDir("desc");
//     }
//   };

//   const updateTargetX = async() => {
//     try {
//       if (!targetX) {
//         alert("Please enter a multiplier.");
//         return;
//       }

//       const res = await apiFetch(`/api/deposits/admin/targetx`, {
//         method: "POST",
//         body: JSON.stringify({multiplier: targetX })
//       });

//       if (!res?.success) {
//         throw new Error(res?.message || "Failed to update multiplier");
//       }

//       fetchData();
//     } catch (err: any) {
//       alert(err.message || "Something went wrong");
//     }
//   };

//   const updateUserTargetX = async() => {
//     try {
//       if (!userTargetX || !editingUser) {
//         alert("Please enter a valid multiplier.");
//         return;
//       }

//       const res = await apiFetch(`/api/deposits/admin/targetx/${editingUser.id}`, {
//         method: "POST",
//         body: JSON.stringify({multiplier: userTargetX })
//       });

//       if (!res?.success) {
//         throw new Error(res?.message || "Failed to update user multiplier");
//       }

//       setEditingUser(null);
//       fetchData();
//     } catch (err: any) {
//       alert(err.message || "Something went wrong");
//     }
//   };

//   return (
//     <div className="ml-0 lg:ml-[256px] p-2">
//       <div className="max-w-7xl mx-auto p-6 space-y-6 bg-gray-50 min-h-screen">
//         <div className="flex items-center justify-between">
//           <h1 className="text-3xl font-bold text-gray-900">User Turnover Report</h1>
//           <button
//             onClick={fetchData}
//             className="inline-flex items-center gap-2 rounded-lg bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition"
//           >
//             <RefreshCw className="w-5 h-5" /> Refresh
//           </button>
//         </div>

//         <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
//           <div className="relative w-full sm:w-80">
//             <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
//             <input
//               value={q}
//               onChange={(e) => { setPage(1); setQ(e.target.value); }}
//               placeholder="Search by name, email, or ID"
//               className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           <div className="flex items-center gap-2">
//             <input
//               type="number"
//               min="1"
//               value={targetX}
//               onChange={(e) => setTargetX(Number(e.target.value))}
//               className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="3"
//             />
//             <button
//               onClick={updateTargetX}
//               className="rounded-lg bg-green-600 text-white px-4 py-2 hover:bg-green-700 transition"
//             >
//               Update TargetX
//             </button>
//           </div>

//           <div className="flex items-center gap-2">
//             <label className="text-sm text-gray-600">Sort by</label>
//             <select
//               value={String(sortKey)}
//               onChange={(e) => setSortKey(e.target.value as keyof WagerRow)}
//               className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
//             >
//               {columns.map(c => (
//                 <option key={c.key} value={c.key}>{c.label}</option>
//               ))}
//             </select>
//             <button
//               onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
//               className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
//               title="Toggle sort direction"
//             >
//               {sortDir === "asc" ? "Asc" : "Desc"}
//             </button>
//           </div>
//         </div>

//         {loading ? (
//           <div className="text-gray-600 text-lg animate-pulse">Loading wagers…</div>
//         ) : error ? (
//           <div className="text-red-600">{error}</div>
//         ) : filtered.length === 0 ? (
//           <div className="text-gray-600">No results.</div>
//         ) : (
//           <div className="overflow-x-auto bg-white rounded-lg shadow">
//             <table className="min-w-full text-sm">
//               <thead className="bg-gray-100 text-gray-700">
//                 <tr>
//                   {columns.map(c => (
//                     <th
//                       key={c.key}
//                       onClick={() => onSort(c.key as keyof WagerRow)}
//                       className="px-4 py-3 font-semibold text-left cursor-pointer select-none hover:bg-gray-200 transition"
//                     >
//                       <span className="inline-flex items-center gap-1">
//                         {c.label}
//                         {sortKey === c.key && (
//                           <ChevronDown className={`w-4 h-4 transition-transform ${sortDir === "desc" ? "rotate-180" : ""}`} />
//                         )}
//                       </span>
//                     </th>
//                   ))}
//                   <th className="px-4 py-3 font-semibold text-left">Progress</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {paged.map((r) => {
//                   const pct = clampPct(r.target_percentage);
//                   const over = r.target_percentage > 100 ? `+${(r.target_percentage - 100).toFixed(1)}%` : "";

//                   return (
//                     <tr key={r.id} className="border-t hover:bg-gray-50 transition">
//                       <td className="px-4 py-3 whitespace-nowrap text-gray-900">{r.id}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">{r.name}</td>
//                       <td className="px-4 py-3 whitespace-nowrap text-gray-700">{r.email}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">{fmtAmt(r.wager)}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">{fmtAmt(r.total_deposit)}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">
//                         <div className="flex items-center gap-2">
//                           <span>{fmtAmt(r.target)}</span>
//                           <button
//                             onClick={() => {
//                               setEditingUser(r);
//                               setUserTargetX(targetX);
//                             }}
//                             className="text-blue-600 hover:text-blue-800 transition"
//                             title="Edit target multiplier"
//                           >
//                             <Pencil className="w-4 h-4" />
//                           </button>
//                         </div>
//                       </td>
//                       <td className="px-4 py-3 whitespace-nowrap">
//                         <span className={r.target_percentage >= 100 ? "text-green-600 font-medium" : ""}>
//                           {r.target_percentage.toFixed(2)}%
//                           {over && <span className="text-green-500 ml-1"> {over}</span>}
//                         </span>
//                       </td>
//                       <td className="px-4 py-3">
//                         <div className="flex flex-col gap-1 min-w-[200px]">
//                           <div className="w-full h-3 rounded-full bg-gray-200 overflow-hidden relative">
//                             <div
//                               className={`h-full transition-all duration-300 ${r.target_percentage >= 100 ? "bg-green-500" : "bg-blue-600"}`}
//                               style={{ width: `${pct}%` }}
//                             />
//                             {r.target_percentage > 100 && (
//                               <div className="absolute inset-0 flex items-center justify-end pr-1">
//                                 <span className="text-xs text-white font-bold">100%+</span>
//                               </div>
//                             )}
//                           </div>
//                           <div className="flex items-center justify-between text-xs text-gray-600">
//                             <span>{fmtAmt(r.wager)}</span>
//                             <span className="font-medium">{r.target_percentage.toFixed(1)}%</span>
//                             <span>{fmtAmt(r.target)}</span>
//                           </div>
//                         </div>
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>
//         )}

//         {filtered.length > 0 && (
//           <div className="flex items-center justify-between text-sm text-gray-600">
//             <span>
//               Showing {Math.min((page - 1) * pageSize + 1, filtered.length)}–
//               {Math.min(page * pageSize, filtered.length)} of {filtered.length}
//             </span>
//             <div className="flex items-center gap-2">
//               <button
//                 disabled={page <= 1}
//                 onClick={() => setPage(p => Math.max(1, p - 1))}
//                 className="px-3 py-1 rounded-lg border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
//               >Previous</button>
//               <span>Page {page} / {totalPages}</span>
//               <button
//                 disabled={page >= totalPages}
//                 onClick={() => setPage(p => Math.min(totalPages, p + 1))}
//                 className="px-3 py-1 rounded-lg border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
//               >Next</button>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Edit User Target Popup */}
//       {editingUser && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
//             <div className="flex items-center justify-between mb-4">
//               <h3 className="text-xl font-semibold text-gray-900">
//                 Edit Target Multiplier
//               </h3>
//               <button
//                 onClick={() => setEditingUser(null)}
//                 className="text-gray-400 hover:text-gray-600 transition"
//               >
//                 <X className="w-6 h-6" />
//               </button>
//             </div>

//             <div className="space-y-4">
//               <div>
//                 <p className="text-sm text-gray-600">User ID: <span className="font-medium text-gray-900">{editingUser.id}</span></p>
//                 <p className="text-sm text-gray-600">Name: <span className="font-medium text-gray-900">{editingUser.name}</span></p>
//                 <p className="text-sm text-gray-600">Email: <span className="font-medium text-gray-900">{editingUser.email}</span></p>
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Target Multiplier
//                 </label>
//                 <input
//                   type="number"
//                   min="1"
//                   value={userTargetX}
//                   onChange={(e) => setUserTargetX(Number(e.target.value))}
//                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   placeholder="3"
//                 />
//               </div>

//               <div className="flex gap-3">
//                 <button
//                   onClick={() => setEditingUser(null)}
//                   className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   onClick={updateUserTargetX}
//                   className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
//                 >
//                   Update TargetX
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default function UserWagerReports() {
//   const [rows, setRows] = useState<WagerRow[]>([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState<string | null>(null);
//   const [q, setQ] = useState("");
//   const [sortKey, setSortKey] = useState<keyof WagerRow>("target_percentage");
//   const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
//   const [page, setPage] = useState(1);
//   const [targetX, setTargetX] = useState<number>(3)
//   const pageSize = 10;

//   // ──────────────────────────────────────────────────────────────
//   //  Fetch Data
//   // ──────────────────────────────────────────────────────────────

//   const columns = [
//     { key: "id", label: "User ID" },
//     { key: "name", label: "Name" },
//     { key: "email", label: "Email" },
//     { key: "wager", label: "Wager" },
//     { key: "total_deposit", label: "Total Deposit" },
//     { key: "target", label: `Targetx(${targetX})` },
//     { key: "target_percentage", label: "% Achieved" },
//   ] as const;


//   const fetchData = async () => {
//     try {
//       setLoading(true);
//       setError(null);

//       // Main request
//       const res = await apiFetch<ApiResponse>("/api/deposits/admin/all-wagers");
//       const res2 = await apiFetch<ApiResponse>("/api/deposits/admin/common-targetx");


//       if (!res?.success || !res2?.success) throw new Error("Failed to fetch data");

//       setRows(res.data || []);
//       setTargetX(res2.data[0]?.multiplier);

//     } catch (e: any) {
//       // Fallback
//       try {
//         const fallback1 = await apiFetch<ApiResponse>("https://api.growl.games/api/deposits/admin/all-wagers");
//         const fallback2 = await apiFetch<ApiResponse>("https://api.growl.games/api/deposits/admin/common-targetx");

//         if (!fallback1?.success || !fallback2?.success) throw new Error("Fallback failed");

//         setRows(fallback1.data || []);
//         // setTargetX(fallback2.data || []);

//       } catch (e2: any) {
//         setError(e2?.message || "Failed to load data");
//       }
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchData();
//   }, []);


//   useEffect(() => {
//     fetchData();
//   }, []);

//   // ──────────────────────────────────────────────────────────────
//   //  Filtering & Sorting
//   // ──────────────────────────────────────────────────────────────
//   const filtered = useMemo(() => {
//     const term = q.trim().toLowerCase();
//     const base = term
//       ? rows.filter(r =>
//           r.name.toLowerCase().includes(term) ||
//           r.email.toLowerCase().includes(term) ||
//           String(r.id).includes(term)
//         )
//       : rows;

//     const sorted = [...base].sort((a, b) => {
//       const va = a[sortKey];
//       const vb = b[sortKey];
//       if (va == null && vb == null) return 0;
//       if (va == null) return sortDir === "asc" ? -1 : 1;
//       if (vb == null) return sortDir === "asc" ? 1 : -1;

//       if (typeof va === "number" && typeof vb === "number") {
//         return sortDir === "asc" ? va - vb : vb - va;
//       }

//       const sa = String(va).toLowerCase();
//       const sb = String(vb).toLowerCase();
//       return sortDir === "asc" ? (sa < sb ? -1 : 1) : (sa > sb ? -1 : 1);
//     });

//     return sorted;
//   }, [rows, q, sortKey, sortDir]);

//   const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
//   const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

//   console.log(paged, 'paged------=-----990')

//   const onSort = (key: keyof WagerRow) => {
//     if (key === sortKey) {
//       setSortDir(d => (d === "asc" ? "desc" : "asc"));
//     } else {
//       setSortKey(key);
//       setSortDir("desc");
//     }
//   };


//   const updateTargetX = async() => {

//       try {
//         if (!targetX) {
//           alert("Please enter a multiplier.");
//           return;
//         }



//         const res = await apiFetch(`/api/deposits/admin/targetx`, {
//           method: "POST",
//           body: JSON.stringify({multiplier: targetX })
//         });

//         if (!res?.success) {
//           throw new Error(res?.message || "Failed to update multiplier");
//         }

//         // alert("Multiplier updated successfully!");
//         fetchData(); // refresh your table
//       } catch (err: any) {
//         alert(err.message || "Something went wrong");
//       }
//     };
//   // ──────────────────────────────────────────────────────────────
//   //  Render
//   // ──────────────────────────────────────────────────────────────
//   return (
//     <div className="ml-0 lg:ml-[256px] p-2">
//       <div className="max-w-7xl mx-auto p-6 space-y-6 bg-gray-50 min-h-screen">
//       <div className="flex items-center justify-between">
//   <h1 className="text-3xl font-bold text-gray-900">User Turnover Report</h1>


//   <button
//     onClick={fetchData}
//     className="inline-flex items-center gap-2 rounded-lg bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition"
//   >
//     <ArrowPathIcon className="w-5 h-5" /> Refresh
//   </button>
// </div>


//         {/* Controls */}
//         <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
//           <div className="relative w-full sm:w-80">
//             <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
//             <input
//               value={q}
//               onChange={(e) => { setPage(1); setQ(e.target.value); }}
//               placeholder="Search by name, email, or ID"
//               className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />

//           </div>
//           <div className="flex items-center gap-2">
//     <input
//       type="text"
//       min="1"
//       value={targetX}
//       onChange={(e) => setTargetX(Number(e.target.value))}
//       className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//       placeholder="3"
//     />

//     <button
//       onClick={updateTargetX}
//       className="rounded-lg bg-green-600 text-white px-4 py-2 hover:bg-green-700 transition"
//     >
//       Update TargetX
//     </button>
//   </div>
//           <div className="flex items-center gap-2">
//             <label className="text-sm text-gray-600">Sort by</label>
//             <select
//               value={String(sortKey)}
//               onChange={(e) => setSortKey(e.target.value as keyof WagerRow)}
//               className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
//             >
//               {columns.map(c => (
//                 <option key={c.key} value={c.key}>{c.label}</option>
//               ))}
//             </select>
//             <button
//               onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
//               className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
//               title="Toggle sort direction"
//             >
//               {sortDir === "asc" ? "Asc" : "Desc"}
//             </button>
//           </div>
//         </div>

//         {/* Content */}
//         {loading ? (
//           <div className="text-gray-600 text-lg animate-pulse">Loading wagers…</div>
//         ) : error ? (
//           <div className="text-red-600">{error}</div>
//         ) : filtered.length === 0 ? (
//           <div className="text-gray-600">No results.</div>
//         ) : (
//           <div className="overflow-x-auto bg-white rounded-lg shadow">
//             <table className="min-w-full text-sm">
//               <thead className="bg-gray-100 text-gray-700">
//                 <tr>
//                   {columns.map(c => (
//                     <th
//                       key={c.key}
//                       onClick={() => onSort(c.key as keyof WagerRow)}
//                       className="px-4 py-3 font-semibold text-left cursor-pointer select-none hover:bg-gray-200 transition"
//                     >
//                       <span className="inline-flex items-center gap-1">
//                         {c.label}
//                         {sortKey === c.key && (
//                           <ChevronDownIcon className={`w-4 h-4 transition-transform ${sortDir === "desc" ? "rotate-180" : ""}`} />
//                         )}
//                       </span>
//                     </th>
//                   ))}
//                   <th className="px-4 py-3 font-semibold text-left">Progress</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {paged.map((r) => {
//                   const pct = clampPct(r.target_percentage); // 0–100
//                   const over = r.target_percentage > 100 ? `+${(r.target_percentage - 100).toFixed(1)}%` : "";

//                   return (
//                     <tr key={r.id} className="border-t hover:bg-gray-50 transition">
//                       <td className="px-4 py-3 whitespace-nowrap text-gray-900">{r.id}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">{r.name}</td>
//                       <td className="px-4 py-3 whitespace-nowrap text-gray-700">{r.email}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">{fmtAmt(r.wager)}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">{fmtAmt(r.total_deposit)}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">{fmtAmt(r.target)}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">
//                         <span className={r.target_percentage >= 100 ? "text-green-600 font-medium" : ""}>
//                           {r.target_percentage.toFixed(2)}%
//                           {over && <span className="text-green-500 ml-1"> {over}</span>}
//                         </span>
//                       </td>
//                       <td className="px-4 py-3">
//                         <div className="flex flex-col gap-1 min-w-[200px]">
//                           <div className="w-full h-3 rounded-full bg-gray-200 overflow-hidden relative">
//                             <div
//                               className={`h-full transition-all duration-300 ${r.target_percentage >= 100 ? "bg-green-500" : "bg-blue-600"}`}
//                               style={{ width: `${pct}%` }}
//                             />
//                             {r.target_percentage > 100 && (
//                               <div className="absolute inset-0 flex items-center justify-end pr-1">
//                                 <span className="text-xs text-white font-bold">100%+</span>
//                               </div>
//                             )}
//                           </div>
//                           <div className="flex items-center justify-between text-xs text-gray-600">
//                             <span>{fmtAmt(r.wager)}</span>
//                             <span className="font-medium">{r.target_percentage.toFixed(1)}%</span>
//                             <span>{fmtAmt(r.target)}</span>
//                           </div>
//                         </div>
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>
//         )}

//         {/* Pagination */}
//         {filtered.length > 0 && (
//           <div className="flex items-center justify-between text-sm text-gray-600">
//             <span>
//               Showing {Math.min((page - 1) * pageSize + 1, filtered.length)}–
//               {Math.min(page * pageSize, filtered.length)} of {filtered.length}
//             </span>
//             <div className="flex items-center gap-2">
//               <button
//                 disabled={page <= 1}
//                 onClick={() => setPage(p => Math.max(1, p - 1))}
//                 className="px-3 py-1 rounded-lg border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
//               >Previous</button>
//               <span>Page {page} / {totalPages}</span>
//               <button
//                 disabled={page >= totalPages}
//                 onClick={() => setPage(p => Math.min(totalPages, p + 1))}
//                 className="px-3 py-1 rounded-lg border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
//               >Next</button>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }


// import React, { useEffect, useMemo, useState } from "react";
// import { ChevronDownIcon, ArrowPathIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
// import { apiFetch } from "../utils/api";

// // ──────────────────────────────────────────────────────────────
// //  UserWagerReports.tsx
// //  • Fetches and displays wager progress for all users
// //  • Progress bar fills based on wager vs first_deposit_amount_2x
// //  • Search, sort, and client-side pagination included
// //  • Matches the lightweight, Tailwind-first style of your codebase
// // ──────────────────────────────────────────────────────────────

// type WagerRow = {
//   id: number;
//   name: string;
//   email: string;
//   wager: number; // current wager volume
//   first_deposit_amount: number; // 1x
//   first_deposit_amount_2x: number; // target (max)
//   first_deposit_at: string | null;
//   user_created_at: string;
// };

// type ApiResponse = {
//   success: boolean;
//   data: WagerRow[];
// };

// const fmtAmt = (n: number | null | undefined) => {
//   if (n == null || Number.isNaN(n)) return "-";
//   return new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format(n);
// };

// const fmtDateTime = (iso: string | null) => {
//   if (!iso) return "-";
//   const d = new Date(iso);
//   if (Number.isNaN(d.getTime())) return "-";
//   return d.toLocaleString();
// };

// const clamp = (n: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, n));

// const columns = [
//   { key: "id", label: "User ID" },
//   { key: "name", label: "Name" },
//   { key: "email", label: "Email" },
//   { key: "wager", label: "Wager" },

// ];

// export default function UserWagerReports() {
//   const [rows, setRows] = useState<WagerRow[]>([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState<string | null>(null);
//   const [q, setQ] = useState("");
//   const [sortKey, setSortKey] = useState<keyof WagerRow>("first_deposit_amount_2x");
//   const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
//   const [page, setPage] = useState(1);
//   const pageSize = 10;

//   const fetchData = async () => {
//     try {
//       setLoading(true);
//       setError(null);
//       // If your apiFetch is already configured with a base, you can drop the host part.
//       const res = await apiFetch<ApiResponse>("/api/deposits/admin/all-wagers");
//       if (!res || !(res as any).success) throw new Error("Unexpected response");
//       setRows((res as ApiResponse).data || []);
//     } catch (e: any) {
//       // fallback to absolute URL if relative fails
//       try {
//         const fallback = await apiFetch<ApiResponse>(
//           "https://api.jstlive.com/api/deposits/admin/all-wagers"
//         );
//         if (!fallback || !(fallback as any).success) throw new Error("Unexpected response");
//         setRows((fallback as ApiResponse).data || []);
//       } catch (e2: any) {
//         setError(e2?.message || "Failed to load wagers");
//       }
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchData();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const filtered = useMemo(() => {
//     const term = q.trim().toLowerCase();
//     const base = term
//       ? rows.filter(r =>
//           r.name.toLowerCase().includes(term) ||
//           r.email.toLowerCase().includes(term) ||
//           String(r.id).includes(term)
//         )
//       : rows;

//     const sorted = [...base].sort((a, b) => {
//       const va = a[sortKey];
//       const vb = b[sortKey];
//       if (va == null && vb == null) return 0;
//       if (va == null) return sortDir === "asc" ? -1 : 1;
//       if (vb == null) return sortDir === "asc" ? 1 : -1;
//       if (typeof va === "number" && typeof vb === "number") {
//         return sortDir === "asc" ? va - vb : vb - va;
//       }
//       const sa = String(va).toLowerCase();
//       const sb = String(vb).toLowerCase();
//       if (sa < sb) return sortDir === "asc" ? -1 : 1;
//       if (sa > sb) return sortDir === "asc" ? 1 : -1;
//       return 0;
//     });

//     return sorted;
//   }, [rows, q, sortKey, sortDir]);

//   const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
//   const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

//   const onSort = (key: keyof WagerRow) => {
//     if (key === sortKey) {
//       setSortDir(d => (d === "asc" ? "desc" : "asc"));
//     } else {
//       setSortKey(key);
//       setSortDir("asc");
//     }
//   };

//   return (
//     <div className="ml-0 lg:ml-[256px] p-2">
//       <div className="max-w-7xl mx-auto p-6 space-y-6 bg-gray-50 min-h-screen">
//         <div className="flex items-center justify-between">
//           <h1 className="text-3xl font-bold text-gray-900">User Turnover Report</h1>
//           <button
//             onClick={fetchData}
//             className="inline-flex items-center gap-2 rounded-lg bg-blue-600 text-white px-4 py-2 hover:bg-blue-700"
//           >
//             <ArrowPathIcon className="w-5 h-5" /> Refresh
//           </button>
//         </div>

//         {/* Controls */}
//         <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
//           <div className="relative w-full sm:w-80">
//             <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
//             <input
//               value={q}
//               onChange={(e) => { setPage(1); setQ(e.target.value); }}
//               placeholder="Search by name, email, or ID"
//               className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           <div className="flex items-center gap-2">
//             <label className="text-sm text-gray-600">Sort by</label>
//             <select
//               value={String(sortKey)}
//               onChange={(e) => setSortKey(e.target.value as keyof WagerRow)}
//               className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
//             >
//               {columns.map(c => (
//                 <option key={c.key} value={c.key}>{c.label}</option>
//               ))}
//             </select>
//             <button
//               onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
//               className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
//               title="Toggle sort direction"
//             >
//               {sortDir === "asc" ? "Asc" : "Desc"}
//             </button>
//           </div>
//         </div>

//         {/* Content */}
//         {loading ? (
//           <div className="text-gray-600 text-lg animate-pulse">Loading wagers…</div>
//         ) : error ? (
//           <div className="text-red-600">{error}</div>
//         ) : filtered.length === 0 ? (
//           <div className="text-gray-600">No results.</div>
//         ) : (
//           <div className="overflow-x-auto bg-white rounded-lg shadow">
//             <table className="min-w-full text-sm">
//               <thead className="bg-gray-100 text-gray-700">
//                 <tr>
//                   {columns.map(c => (
//                     <th
//                       key={c.key}
//                       onClick={() => onSort(c.key as keyof WagerRow)}
//                       className="px-4 py-3 font-semibold text-left cursor-pointer select-none"
//                     >
//                       <span className="inline-flex items-center gap-1">
//                         {c.label}
//                         {sortKey === c.key && (
//                           <ChevronDownIcon className={`w-4 h-4 transition-transform ${sortDir === "desc" ? "rotate-180" : ""}`} />
//                         )}
//                       </span>
//                     </th>
//                   ))}
//                   <th className="px-4 py-3 font-semibold text-left">Progress (Wager → 2×)</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {paged.map((r) => {
//                   const max = r.first_deposit_amount_2x || 0;
//                   const cur = r.wager || 0;
//                   const pct = max > 0 ? clamp(cur / max) : 0;
//                   const pctText = `${(pct * 100).toFixed(2)}%`;

//                   return (
//                     <tr key={r.id} className="border-t last:border-b">
//                       <td className="px-4 py-3 whitespace-nowrap text-gray-900">{r.id}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">{r.name}</td>
//                       <td className="px-4 py-3 whitespace-nowrap text-gray-700">{r.email}</td>
//                       <td className="px-4 py-3 whitespace-nowrap">{fmtAmt(r.wager)}</td>

//                       <td className="px-4 py-3">
//                         {/* Slider-style progress bar */}
//                         <div className="flex flex-col gap-1 min-w-[240px]">
//                           <div className="w-full h-3 rounded-full bg-gray-200 overflow-hidden">
//                             <div
//                               className="h-full bg-blue-600"
//                               style={{ width: `${pct * 100}%` }}
//                               aria-valuemin={0}
//                               aria-valuemax={100}
//                               aria-valuenow={pct * 100}
//                               role="progressbar"
//                             />
//                           </div>
//                           <div className="flex items-center justify-between text-xs text-gray-600">
//                             <span>Wager: {fmtAmt(cur)}</span>
//                             <span>{pctText}</span>
//                             <span>Target: {fmtAmt(max)}</span>
//                           </div>
//                           {/* Visual (non-interactive) range to mimic a slider */}
//                           {/* <input
//                             type="range"
//                             min={0}
//                             max={max || 1}
//                             value={Math.min(cur, max || 0)}
//                             readOnly
//                             className="w-full accent-blue-600 cursor-default"
//                           /> */}
//                         </div>
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>
//         )}

//         {/* Pagination */}
//         {filtered.length > 0 && (
//           <div className="flex items-center justify-between text-sm text-gray-600">
//             <span>
//               Showing {Math.min((page - 1) * pageSize + 1, filtered.length)}–
//               {Math.min(page * pageSize, filtered.length)} of {filtered.length}
//             </span>
//             <div className="flex items-center gap-2">
//               <button
//                 disabled={page <= 1}
//                 onClick={() => setPage(p => Math.max(1, p - 1))}
//                 className="px-3 py-1 rounded-lg border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
//               >Previous</button>
//               <span>Page {page} / {totalPages}</span>
//               <button
//                 disabled={page >= totalPages}
//                 onClick={() => setPage(p => Math.min(totalPages, p + 1))}
//                 className="px-3 py-1 rounded-lg border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
//               >Next</button>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }
